import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Portal,
  Button,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClientDocumentCountService from '../../Services/ClientDocumentCount.service';
import EnumsService from '../../Services/enums.service';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import styles from './UnprocessedItemsModal.module.scss';
import { splitOtherDocumentTypes } from '../../Utils/otherDocumentSections';

/** Map backend DocType to sectionKey used by EditModal / TransactionSearchResultModal */
function getSectionKey(docType) {
  if (docType === 'Checks') return 'PaymentAcceptances';
  if (docType === 'OtherDocuments') return 'OtherDocuments';
  return docType; // Incomes, Expenses, JournalEntries
}

/** Parse "MM/yy" to start/end dates for the month. Use noon local to avoid UTC rollback to previous day. */
function getMonthStartAndEnd(monthYear) {
  const [month, year] = monthYear.split('/').map(Number);
  const fullYear = year < 100 ? 2000 + year : year;
  const start = new Date(fullYear, month - 1, 1, 12, 0, 0);
  const end = new Date(fullYear, month, 0, 12, 0, 0);
  return { start, end };
}

const SUMMARY_CARD_CONFIG = [
  { key: 'incomes', docType: 'Incomes', labelKey: 'Incomes', cardClass: 'summaryCardIncomes', navigateTo: 'Document' },
  { key: 'expenses', docType: 'Expenses', labelKey: 'Expenses', cardClass: 'summaryCardExpenses', navigateTo: 'Document' },
  { key: 'journalEntries', docType: 'JournalEntries', labelKey: 'JournalEntry', cardClass: 'summaryCardJournalEntries', navigateTo: 'JournalEntry' },
  { key: 'checks', docType: 'Checks', labelKey: 'PaymentAcceptance', cardClass: 'summaryCardChecks', navigateTo: 'PaymentAcceptance' },
];

export default function UnprocessedItemsModal({ open, onClose, client, caseId, token }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const navigate = useNavigate();
  const { handleDocumentRowClick, setRefreshUnprocessedPreview } = useHamburgerMenu();
  const [loading, setLoading] = useState(false);
  const [summaryByMonth, setSummaryByMonth] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [otherDocsTypes, setOtherDocsTypes] = useState([]);
  const [otherDocsCounts, setOtherDocsCounts] = useState({});
  const [otherDocsTitles, setOtherDocsTitles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** When user opens edit/PA from a row, store so we can reopen after refresh (save) */
  const [lastOpenedForReopen, setLastOpenedForReopen] = useState(null);
  const lastOpenedForReopenRef = useRef(null);
  const pendingReopenRef = useRef(null);

  useEffect(() => {
    lastOpenedForReopenRef.current = lastOpenedForReopen;
  }, [lastOpenedForReopen]);

  const fetchPreview = useCallback(async () => {
    if (!caseId || !token) return;
    setLoading(true);
    try {
      const data = await ClientDocumentCountService.getUnprocessedPreview(caseId, token);
      const summary = data.summaryByReportingMonth ?? data.SummaryByReportingMonth ?? [];
      const txns = (data.transactions ?? data.Transactions ?? []).map((row) => ({
        id: row.id ?? row.Id,
        docType: row.docType ?? row.DocType,
        documentDate: row.documentDate ?? row.DocumentDate,
        documentType: row.documentType ?? row.DocumentType ?? '',
        amount: row.amount ?? row.Amount ?? 0,
        description: row.description ?? row.Description ?? '',
        reportingMonth: row.reportingMonth ?? row.ReportingMonth ?? '—',
        sortCodeId: row.sortCodeId ?? row.SortCodeId ?? '',
        sortCodeName: row.sortCodeName ?? row.SortCodeName ?? '',
        debitAccount: row.debitAccount ?? row.DebitAccount ?? '',
        debitAccountName: row.debitAccountName ?? row.DebitAccountName ?? '',
        creditAccount: row.creditAccount ?? row.CreditAccount ?? '',
        creditAccountName: row.creditAccountName ?? row.CreditAccountName ?? '',
      }));
      setSummaryByMonth(summary);
      setTransactions(txns);
    } catch (err) {
      console.error('UnprocessedItemsModal fetch error:', err);
      setSummaryByMonth([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, token]);

  const fetchOtherDocumentsData = useCallback(async () => {
    if (!client?.accountantId || !client?.caseId || !token) return;
    try {
      const [typesRes, countsRes, titlesRes] = await Promise.all([
        EnumsService.GetOtherDocumentsTypesByAccountantId(client.accountantId, token),
        EnumsService.GetOtherDocumentsCountsByCaseId(client.caseId, token),
        EnumsService.GetOtherDocumentsTitlesByAccountantId(client.accountantId, token).catch(() => null),
      ]);
      const typesData = await typesRes.json();
      const countsData = await countsRes.json();
      const titlesData = titlesRes?.ok ? await titlesRes.json().catch(() => []) : [];
      setOtherDocsTypes(Array.isArray(typesData) ? typesData : []);
      setOtherDocsTitles(Array.isArray(titlesData) ? titlesData : []);
      setOtherDocsCounts(countsData && typeof countsData === 'object' ? countsData : {});
    } catch (err) {
      console.error('Failed to fetch OtherDocuments types/counts:', err);
    }
  }, [client, token]);

  useEffect(() => {
    if (open && caseId && token) {
      fetchPreview();
      fetchOtherDocumentsData();
      setPage(0);
    }
  }, [open, caseId, token, fetchPreview, fetchOtherDocumentsData]);

  // Register refresh callback so edit/PA modal can refresh this list after update; after refresh, reopen modal
  useEffect(() => {
    if (!setRefreshUnprocessedPreview) return;
    if (!open) {
      setRefreshUnprocessedPreview(null);
      setLastOpenedForReopen(null);
      return () => {};
    }
    const wrappedRefresh = () => {
      const toReopen = lastOpenedForReopenRef.current;
      if (toReopen?.documentId && toReopen?.sectionKey) {
        pendingReopenRef.current = { documentId: toReopen.documentId, sectionKey: toReopen.sectionKey };
      }
      fetchPreview();
    };
    setRefreshUnprocessedPreview(wrappedRefresh);
    return () => setRefreshUnprocessedPreview(null);
  }, [open, fetchPreview, setRefreshUnprocessedPreview]);

  /** Format "value - name" for table cells; fallback to value or name or — */
  const valueNameLabel = (value, name) => {
    if (value && name && value !== name) return `${value} - ${name}`;
    if (name) return name;
    if (value) return value;
    return '—';
  };

  const docTypeLabel = useCallback((docType) => {
    switch (docType) {
      case 'Incomes': return t('Incomes');
      case 'Expenses': return t('Expenses');
      case 'JournalEntries': return t('JournalEntry');
      case 'Checks': return t('PaymentAcceptance');
      case 'OtherDocuments': return t('OtherDocuments');
      default: return docType;
    }
  }, [t]);

  /** Filtered transactions by search and docType filter */
  const filteredTransactions = useMemo(() => {
    let list = transactions;
    const search = (searchText || '').trim().toLowerCase();
    if (search) {
      list = list.filter((row) => {
        const typeLabel = docTypeLabel(row.docType).toLowerCase();
        const desc = (row.description || '').toLowerCase();
        const amountStr = String(row.amount || '');
        const sortCode = [row.sortCodeId, row.sortCodeName].filter(Boolean).join(' ').toLowerCase();
        const debit = [row.debitAccount, row.debitAccountName].filter(Boolean).join(' ').toLowerCase();
        const credit = [row.creditAccount, row.creditAccountName].filter(Boolean).join(' ').toLowerCase();
        const reportingMonth = (row.reportingMonth || '').toLowerCase();
        return typeLabel.includes(search) || desc.includes(search) || amountStr.includes(search) ||
          sortCode.includes(search) || debit.includes(search) || credit.includes(search) ||
          reportingMonth.includes(search);
      });
    }
    if (filterDocType) {
      list = list.filter((row) => row.docType === filterDocType);
    }
    return list;
  }, [transactions, searchText, filterDocType, docTypeLabel]);

  // When refresh runs after save, reopen the same edit/PA modal with updated list
  useEffect(() => {
    if (pendingReopenRef.current && filteredTransactions.length >= 0) {
      const { documentId, sectionKey } = pendingReopenRef.current;
      pendingReopenRef.current = null;
      if (handleDocumentRowClick && documentId && sectionKey) {
        const enrichedList = filteredTransactions.map(t => ({ ...t, status: 'notDone', isUnprocessed: true }));
        handleDocumentRowClick(documentId, sectionKey, enrichedList);
      }
    }
  }, [transactions, filteredTransactions, handleDocumentRowClick]);

  const activeFiltersCount = (filterDocType ? 1 : 0);

  /** Build per-type cards: each type has list of { month, count } for months that have unprocessed */
  const summaryCards = useMemo(() => {
    return SUMMARY_CARD_CONFIG.map((cfg) => {
      const { key } = cfg;
      const months = (summaryByMonth || [])
        .map((row) => {
          const month = row.reportingMonth ?? row.ReportingMonth;
          const count = row[key] ?? row[key === 'journalEntries' ? 'JournalEntries' : key.charAt(0).toUpperCase() + key.slice(1)] ?? 0;
          return count > 0 ? { month, count } : null;
        })
        .filter(Boolean);
      return { ...cfg, months };
    });
  }, [summaryByMonth]);

  const otherDocumentSections = useMemo(() => {
    const { otherDocuments, permanentFiles, customTitles } = splitOtherDocumentTypes(otherDocsTypes, otherDocsTitles);
    const toItems = (types) => types.map((doc) => ({ label: doc.name, count: otherDocsCounts[doc.name] || 0 }));
    const baseState = { type: 'OtherDocuments', client };
    return [
      { key: 'other', label: t('OtherDocuments'), className: styles.summaryCardOther, navState: { ...baseState, isPermenantCase: false }, items: toItems(otherDocuments) },
      { key: 'permanent', label: t('PermanentFile'), className: styles.summaryCardPermanent, navState: { ...baseState, isPermenantCase: true }, items: toItems(permanentFiles) },
      ...customTitles.map(({ title, types }) => ({
        key: `title-${title.id}`,
        label: title.name,
        className: styles.summaryCardPermanent,
        navState: { ...baseState, isPermenantCase: false, titleId: title.id, titleName: title.name },
        items: toItems(types),
      })),
    ].filter((section) => section.items.length > 0);
  }, [otherDocsTypes, otherDocsTitles, otherDocsCounts, client, t]);

  const NAVIGATE_TO_PATH = {
    JournalEntry: '/JournalEntry',
    PaymentAcceptance: '/PaymentAcceptance',
    Document: '/Document',
  };

  const handleMonthClick = (config, monthYear) => {
    if (!client) return;
    console.log('config', config);
    const { start, end } = getMonthStartAndEnd(monthYear);
    const type = config.docType === 'Checks' ? 'PaymentAcceptances' : config.docType;
    const path = NAVIGATE_TO_PATH[config.navigateTo] || '/Document';
    navigate(path, { state: { type, client, start, end } });
    if (typeof onClose === 'function') onClose();
  };

  const handleRowClick = (item) => {
    if (item.docType === 'OtherDocuments') {
      navigate('/OtherDocument', {
        state: { type: 'OtherDocuments', client, subItem: item.documentType || null, isPermenantCase: false }
      });
      return;
    }
    // Incomes, Expenses, JournalEntries, and Checks (PaymentAcceptance) open edit/PA modal – no navigate.
    // Month chips still navigate via handleMonthClick.
    const sectionKey = getSectionKey(item.docType);
    setLastOpenedForReopen({ documentId: item.id, sectionKey });
    if (handleDocumentRowClick) {
      const enrichedList = filteredTransactions.map(t => ({ ...t, status: 'notDone', isUnprocessed: true }));
      handleDocumentRowClick(item.id, sectionKey, enrichedList);
    }
  };

  const paginatedRows = useMemo(() =>
    filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTransactions, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [searchText, filterDocType]);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  if (!open) return null;

  return (
    <Portal>
      <div
        className={`${styles.overlay} ${dir === 'rtl' ? styles.rtl : styles.ltr}`}
        onClick={onClose}
      >
        <div dir={dir} onClick={(e) => e.stopPropagation()}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unprocessed-items-modal-title"
          >
            <div className={styles.scroll}>
              <div className={styles.header}>
                <Tooltip title={t('Close') || 'Close'}>
                  <IconButton onClick={onClose} className={styles.closeIcon} aria-label={t('Close')}>
                    <CancelIcon className={styles.closeIcon} />
                  </IconButton>
                </Tooltip>
                <div id="unprocessed-items-modal-title">{t('DocumentsAwaitingTreatment')}</div>
                <div />
              </div>

              {loading ? (
                <div className={styles.loading}>
                  <CircularProgress className={styles.spinner} size={36} />
                </div>
              ) : (
                <>
                  {/* Summary as cards by type – click month to navigate to that screen */}
                  {summaryCards.some((c) => c.months.length > 0) && (
                    <div className={styles.sectionTitle}>
                      {t('Summary')} – {t('ReportingMonth', 'חודש דיווח')}
                    </div>
                  )}
                  <div className={styles.cardsGrid}>
                    {summaryCards.map((card) => (
                      <div
                        key={card.key}
                        className={`${styles.summaryCard} ${styles[card.cardClass] || ''}`}
                      >
                        <div className={styles.cardTitle}>{t(card.labelKey)}</div>
                        {card.months.length === 0 ? (
                          <span className={styles.cardEmpty}>—</span>
                        ) : (
                          <div className={styles.chipRow}>
                            {card.months.map(({ month, count }) => (
                              <button
                                key={month}
                                type="button"
                                className={styles.chip}
                                onClick={() => handleMonthClick(card, month)}
                              >
                                {`${month} (${count})`}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* OtherDocuments / PermanentFile with sub-item navigation */}
                  {otherDocumentSections.length > 0 && (
                    <div className={styles.cardsGridTwo}>
                      {otherDocumentSections.map((section) => (
                        <div key={section.key} className={`${styles.summaryCard} ${section.className}`}>
                          <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>{section.label}</div>
                            <IconButton
                              size="small"
                              className={styles.openIcon}
                              onClick={() => navigate('/OtherDocument', { state: { ...section.navState, subItem: null } })}
                              aria-label={section.label}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </div>
                          <div className={styles.chipRow}>
                            {section.items.map((doc) => (
                              <button
                                key={doc.label}
                                type="button"
                                className={styles.chip}
                                onClick={() => navigate('/OtherDocument', { state: { ...section.navState, subItem: doc.label } })}
                              >
                                {`${doc.label} (${doc.count})`}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search and filters */}
                  <div className={styles.toolbar}>
                    <TextField
                      size="small"
                      className={styles.searchField}
                      placeholder={t('Search', 'חיפוש')}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      size="small"
                      startIcon={<FilterListIcon />}
                      onClick={() => setFiltersOpen((o) => !o)}
                      className={`${styles.filterBtn} ${activeFiltersCount > 0 ? styles.filterBtnActive : ''}`}
                      variant={activeFiltersCount > 0 ? 'outlined' : 'text'}
                    >
                      {t('Filters', 'סינון')}
                      {activeFiltersCount > 0 && (
                        <Chip label={activeFiltersCount} size="small" className={styles.filterCount} />
                      )}
                    </Button>
                    {filtersOpen && (
                      <>
                        <FormControl size="small" className={styles.filterSelect}>
                          <InputLabel>{t('Type', 'סוג')}</InputLabel>
                          <Select
                            value={filterDocType}
                            label={t('Type', 'סוג')}
                            onChange={(e) => setFilterDocType(e.target.value)}
                          >
                            <MenuItem value="">{t('All', 'הכל')}</MenuItem>
                            <MenuItem value="Incomes">{t('Incomes')}</MenuItem>
                            <MenuItem value="Expenses">{t('Expenses')}</MenuItem>
                            <MenuItem value="JournalEntries">{t('JournalEntry')}</MenuItem>
                            <MenuItem value="Checks">{t('PaymentAcceptance')}</MenuItem>
                            <MenuItem value="OtherDocuments">{t('OtherDocuments')}</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          className={styles.clearBtn}
                          onClick={() => { setFilterDocType(''); setSearchText(''); }}
                        >
                          {t('Clear', 'ניקוי')}
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Transactions table */}
                  <div className={styles.sectionTitle}>
                    {t('Transactions', 'תנועות')}
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>{t('Type', 'סוג')}</th>
                          <th>{t('DocumentDate', 'תאריך מסמך')}</th>
                          <th className={styles.amountCell}>{t('Amount', 'סכום')}</th>
                          <th>{t('Description', 'תיאור')}</th>
                          <th>{t('ReportingMonth', 'חודש דיווח')}</th>
                          <th>{t('SortCodeId', 'קוד מיון')}</th>
                          <th>{t('DebitAccount', 'חובה')}</th>
                          <th>{t('CreditAccount', 'זכות')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.length === 0 ? (
                          <tr>
                            <td colSpan={8} className={styles.emptyCell}>
                              {t('NoDocuments', 'אין מסמכים')}
                            </td>
                          </tr>
                        ) : (
                          paginatedRows.map((row) => (
                            <tr
                              key={`${row.docType}-${row.id}`}
                              onClick={() => handleRowClick(row)}
                            >
                              <td>{docTypeLabel(row.docType)}</td>
                              <td className={styles.nowrap}>{formatDate(row.documentDate)}</td>
                              <td className={styles.amountCell}>{beautifyNumber(row.amount)}</td>
                              <td>{row.description || '—'}</td>
                              <td>{row.reportingMonth}</td>
                              <td className={styles.nowrap}>{valueNameLabel(row.sortCodeId, row.sortCodeName)}</td>
                              <td className={styles.nowrap}>{valueNameLabel(row.debitAccount, row.debitAccountName)}</td>
                              <td className={styles.nowrap}>{valueNameLabel(row.creditAccount, row.creditAccountName)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredTransactions.length > rowsPerPage && (
                    <TablePagination
                      component="div"
                      className={styles.pagination}
                      count={filteredTransactions.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      labelRowsPerPage={t('RowsPerPage', 'שורות בעמוד')}
                    />
                  )}

                  <div className={styles.footer}>
                    <button type="button" className={styles.cancel} onClick={onClose}>
                      {t('Close')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
